import { useId } from "react";
import { cn } from "@/lib/cn";
import styles from "./Mascot.module.css";

export type MascotMood = "ecstatic" | "happy" | "neutral" | "worried" | "sad";

interface MascotProps {
  mood: MascotMood;
  /** Deslocamento normalizado (-1..1) de para onde os olhos olham. */
  look?: { x: number; y: number };
  size?: number;
  className?: string;
}

const MOOD_TINT: Record<MascotMood, { rim: string; deep: string; glow: string }> = {
  ecstatic: { rim: "#6be6c0", deep: "#1a2420", glow: "#3ddc97" },
  happy: { rim: "#4fb8ff", deep: "#141c26", glow: "#4c7dff" },
  neutral: { rim: "#9fb0c2", deep: "#181c22", glow: "#8a97ab" },
  worried: { rim: "#ffb85c", deep: "#241c14", glow: "#f0a83f" },
  sad: { rim: "#ff7d93", deep: "#241418", glow: "#f0435f" },
};

const EAR_TILT: Record<MascotMood, number> = {
  ecstatic: -10,
  happy: -4,
  neutral: 0,
  worried: 5,
  sad: 10,
};

// Sobrancelha franzida por padrão (já nasce "brava") — o humor só deixa mais ou menos
// intensa, nunca some de vez, pra manter o ar valentão.
const BROW_TILT: Record<MascotMood, number> = {
  ecstatic: -6,
  happy: -2,
  neutral: 2,
  worried: 6,
  sad: 10,
};

const MOUTH_PATH: Record<MascotMood, string> = {
  ecstatic: "M78 172 Q100 192 122 172",
  happy: "M82 172 Q100 183 118 172",
  neutral: "M88 174 Q100 177 112 174",
  worried: "M86 178 Q100 171 114 178",
  sad: "M84 180 Q100 168 116 180",
};

const EYE_L = { cx: 68, cy: 110 };
const EYE_R = { cx: 132, cy: 110 };
// Olho aberto de novo (não mais espremido) — o ar de "mal" vem da sobrancelha franzida
// por cima e da pupila grande e escura, não de um olho quase fechado.
const EYE_R_RADIUS = { rx: 18, ry: 18 };
const PUPIL_RADIUS = 11;

interface EyeProps {
  center: { cx: number; cy: number };
  look: { x: number; y: number };
  gradId: string;
}

function Eye({ center, look, gradId }: EyeProps) {
  const maxOffset = 3;
  const pupilX = center.cx + look.x * maxOffset;
  const pupilY = center.cy + look.y * maxOffset;
  const shineX = pupilX - 4;
  const shineY = pupilY - 5;

  return (
    <g className={styles.eyeBlink} style={{ transformOrigin: `${center.cx}px ${center.cy}px` }}>
      <ellipse cx={center.cx} cy={center.cy} rx={EYE_R_RADIUS.rx} ry={EYE_R_RADIUS.ry} fill={`url(#${gradId})`} className={styles.eyeShape} />
      {/* pupila grande e escura — o que dá o olhar "de mal", não o olho apertado */}
      <circle cx={pupilX} cy={pupilY} r={PUPIL_RADIUS} className={styles.pupilCore} />
      <ellipse cx={shineX} cy={shineY} rx={4} ry={3} className={styles.shine} />
      <ellipse cx={center.cx} cy={center.cy} rx={EYE_R_RADIUS.rx} ry={EYE_R_RADIUS.ry} className={styles.eyeRim} />
    </g>
  );
}

/**
 * Mascote do ControleNext — um javali-cofrinho valentão: orelhas pontudas e eretas,
 * sobrancelha sempre franzida (o humor só varia a intensidade) e pupila grande e escura
 * (sem deixar o olho "fechado" — o ar de mal vem da sobrancelha e da pupila, não de
 * espremer o olho, e sem presas/dentes de fora). A pupila segue o cursor. Vive só dentro
 * do app (widget flutuante) — não aparece mais nas telas de login/cadastro.
 */
export function Mascot({ mood, look = { x: 0, y: 0 }, size = 64, className }: MascotProps) {
  const tint = MOOD_TINT[mood];
  const uid = useId();
  const eyeGradId = `${uid}-eye-${mood}`;
  const bodyGradId = `${uid}-body`;
  const snoutGradId = `${uid}-snout`;
  const earInnerGradId = `${uid}-earinner`;
  const blushOpacity = mood === "ecstatic" ? 0.4 : mood === "happy" ? 0.2 : 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      className={cn(styles.mascot, className)}
      style={{ ["--mood-rim" as string]: tint.rim }}
      aria-hidden
    >
      <defs>
        <radialGradient id={bodyGradId} cx="35%" cy="22%" r="88%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="42%" stopColor="#e7edf5" />
          <stop offset="78%" stopColor="#c3d0e0" />
          <stop offset="100%" stopColor="#9fb0c6" />
        </radialGradient>
        <radialGradient id={snoutGradId} cx="38%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#ffe3e6" />
          <stop offset="55%" stopColor="#f6bfc7" />
          <stop offset="100%" stopColor="#dd97a3" />
        </radialGradient>
        <radialGradient id={earInnerGradId} cx="50%" cy="35%" r="70%">
          <stop offset="0%" stopColor="#ffd9de" />
          <stop offset="100%" stopColor="#e2a3ac" />
        </radialGradient>
        <radialGradient id={eyeGradId} cx="38%" cy="30%" r="75%">
          <stop offset="0%" stopColor={tint.rim} />
          <stop offset="55%" stopColor={tint.deep} />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>
      </defs>

      {/* cabeça inteira (orelhas + rosto) balança de leve o tempo todo — dá vida de idle */}
      <g className={styles.headIdle}>
        {/* orelhas pontudas e eretas — visual mais valentão/javali, não mais caídas e fofas.
            cada orelha ainda treme sozinha de vez em quando, com ritmos diferentes. */}
        <g style={{ transform: `rotate(${EAR_TILT[mood]}deg)`, transformOrigin: "48px 66px" }} className={styles.earShape}>
          <g className={styles.earTwitch} style={{ transformOrigin: "48px 66px" }}>
            <path d="M38 68 C28 46 30 20 48 6 C60 22 64 46 58 70 Z" fill={`url(#${bodyGradId})`} className={styles.earOuter} />
            <path d="M42 62 C36 46 38 28 48 18 C56 30 58 46 54 63 Z" fill={`url(#${earInnerGradId})`} />
          </g>
        </g>
        <g style={{ transform: `rotate(${-EAR_TILT[mood]}deg)`, transformOrigin: "152px 66px" }} className={styles.earShape}>
          <g className={cn(styles.earTwitch, styles.earTwitchRight)} style={{ transformOrigin: "152px 66px" }}>
            <path d="M162 68 C172 46 170 20 152 6 C140 22 136 46 142 70 Z" fill={`url(#${bodyGradId})`} className={styles.earOuter} />
            <path d="M158 62 C164 46 162 28 152 18 C144 30 142 46 146 63 Z" fill={`url(#${earInnerGradId})`} />
          </g>
        </g>

        {/* cabeça */}
        <path
          d="M100 30 C144 30 172 60 172 104 C172 148 144 178 100 178 C56 178 28 148 28 104
             C28 60 56 30 100 30 Z"
          fill={`url(#${bodyGradId})`}
          className={styles.headShape}
        />
        {/* brilho cerâmico no topo da cabeça */}
        <path d="M52 52 C68 36 88 30 100 31 C82 34 66 44 56 62 C50 72 47 82 48 92 C42 78 43 63 52 52 Z" className={styles.headShine} />
        {/* sombra suave de queixo/bochecha, dá volume embaixo do rosto */}
        <path d="M46 140 C58 168 82 178 100 178 C118 178 142 168 154 140 C136 162 118 172 100 172 C82 172 64 162 46 140 Z" className={styles.jawShadow} />

        {/* fenda de moeda, brilha na cor do humor */}
        <rect x={82} y={36} width={36} height={9} rx={4.5} className={styles.slot} />
        <rect x={86} y={38.5} width={28} height={4} rx={2} className={styles.slotGlow} style={{ fill: tint.glow }} />

        {/* sobrancelhas franzidas — nascem "bravas" (formato de V), o humor só varia a intensidade */}
        <path
          d="M42 80 L91 96"
          className={styles.brow}
          style={{ transform: `rotate(${BROW_TILT[mood]}deg)`, transformOrigin: "66px 88px" }}
        />
        <path
          d="M158 80 L109 96"
          className={styles.brow}
          style={{ transform: `rotate(${-BROW_TILT[mood]}deg)`, transformOrigin: "134px 88px" }}
        />

        <Eye center={EYE_L} look={look} gradId={eyeGradId} />
        <Eye center={EYE_R} look={look} gradId={eyeGradId} />

        <circle cx={68} cy={134} r={12} className={styles.blush} style={{ opacity: blushOpacity }} />
        <circle cx={132} cy={134} r={12} className={styles.blush} style={{ opacity: blushOpacity }} />

        {/* focinho: gradiente rosado próprio, sombra de base e brilho, narinas em fenda */}
        <ellipse cx={100} cy={145} rx={30} ry={22} className={styles.snoutShadow} />
        <ellipse cx={100} cy={143} rx={28} ry={20} fill={`url(#${snoutGradId})`} className={styles.snout} />
        <path d="M78 136 Q100 128 122 136" className={styles.snoutShine} />
        <ellipse cx={90} cy={145} rx={3.4} ry={6} className={styles.nostril} />
        <ellipse cx={110} cy={145} rx={3.4} ry={6} className={styles.nostril} />

        <path d={MOUTH_PATH[mood]} className={styles.mouth} />
      </g>
    </svg>
  );
}
